import { useEffect, useMemo, useState } from "react";
import { Alert, Button, Form, Spinner } from "react-bootstrap";
import styled from "styled-components";
import { request } from "hooks/useOkoApi/useOkoApi";
import { logError, LogCategory } from "utils/sentryLogger";
import { colors, constants } from "utils";

const DATASET_PREFIX = constants.BETTER_HINT_DATASET_PREFIX;

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow-x: hidden;
`;

const Controls = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  padding: 16px;
  background: ${colors.white};
  border: 1px solid ${colors.milkGlass400};
  border-radius: 8px;
`;

const ControlField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 240px;
  flex: 1;
`;

const ItemCard = styled.div`
  background: ${colors.white};
  border: 1px solid ${colors.milkGlass400};
  border-radius: 8px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
  max-width: 100%;
  overflow-x: auto;
  word-break: break-word;
`;

const ItemHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ItemIndex = styled.span`
  font-weight: 600;
  color: ${colors.blue600 || "#4f46e5"};
`;

const ItemId = styled.span`
  font-family: "SF Mono", Menlo, monospace;
  font-size: 12px;
  color: ${colors.milkGlass600 || "#6b7280"};
`;

const ContextDetails = styled.details`
  border: 1px solid ${colors.milkGlass400};
  border-radius: 6px;
  background: ${colors.milkGlass100 || "#fafafa"};
  & > summary {
    cursor: pointer;
    padding: 8px 12px;
    font-size: 13px;
    color: ${colors.milkGlass600 || "#6b7280"};
    user-select: none;
  }
`;

const ContextDetailsBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 12px;
`;

const QuestionBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const QuestionImage = styled.img`
  max-width: 320px;
  max-height: 240px;
  object-fit: contain;
  align-self: flex-start;
  border: 1px solid ${colors.milkGlass400};
  border-radius: 6px;
`;

const RawText = styled.pre`
  padding: 10px 12px;
  font-size: 12px;
  white-space: pre-wrap;
  word-break: break-word;
  margin: 0;
  font-family: "SF Mono", Menlo, monospace;
`;

const FieldLabel = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6b7280;
  font-weight: 600;
`;

const FieldValue = styled.div`
  font-size: 13px;
  white-space: pre-wrap;
  word-break: break-word;
`;

const ColumnsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(${(p) => p.$columns}, minmax(0, 1fr));
  gap: 12px;
`;

const Column = styled.div`
  border: 1px solid ${colors.milkGlass400};
  border-radius: 6px;
  padding: 12px;
  background: #fafafa;
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
`;

const ColumnTitle = styled.div`
  font-weight: 600;
  font-size: 13px;
  color: ${(p) => p.color || "#1f2330"};
  padding-bottom: 6px;
  border-bottom: 1px solid ${colors.milkGlass400};
`;

const NoVal = styled.span`
  color: ${colors.milkGlass600 || "#6b7280"};
  font-size: 13px;
`;

const HintText = styled.div`
  font-size: 14px;
  white-space: pre-wrap;
  word-break: break-word;
`;

const WordCount = styled.span`
  margin-left: 8px;
  text-transform: none;
  letter-spacing: normal;
  font-weight: 500;
`;

const Pills = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const PillBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  background: #eef2ff;
  color: #3730a3;
  font-size: 12px;
  font-weight: 600;
`;

const ColumnDetails = styled.details`
  border: 1px solid ${colors.milkGlass400};
  border-radius: 6px;
  background: #fff;
  & > summary {
    cursor: pointer;
    padding: 6px 10px;
    font-size: 12px;
    color: ${colors.milkGlass600 || "#6b7280"};
    user-select: none;
  }
`;

const ColumnDetailsBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 10px;
`;

const AdditionalBody = styled.pre`
  margin: 0;
  padding: 8px 10px;
  font-size: 11px;
  white-space: pre-wrap;
  word-break: break-word;
  font-family: "SF Mono", Menlo, monospace;
`;

const HINT_WORD_LIMIT = 60;
const ALERT_RED = "#dc2626";

const EXPECTED_COLOR = "#475569";
const RUN_A_COLOR = "#0ea5e9";
const RUN_B_COLOR = "#d97706";

const formatLatency = (ms) => {
  if (ms === null || ms === undefined) return "—";
  if (ms < 1000) return `${ms.toFixed(0)} ms`;
  return `${(ms / 1000).toFixed(2)} s`;
};

const shortRunLabel = (runName) => {
  if (!runName) return "";
  const beforeComma = runName.split(",")[0].trim();
  return beforeComma.length > 40 ? `${beforeComma.slice(0, 40)}…` : beforeComma;
};

const SummaryRow = styled.div`
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
  padding: 16px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
`;

const SummaryCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 220px;
`;

const SummaryTitle = styled.div`
  font-weight: 600;
  font-size: 13px;
  color: ${(p) => p.color || "#1f2330"};
`;

const SummaryStat = styled.div`
  font-size: 13px;
  display: flex;
  justify-content: space-between;
  gap: 12px;
  & > .label { color: #6b7280; }
  & > .val { font-family: "SF Mono", Menlo, monospace; }
`;

const renderField = (label, value) => (
  <div>
    <FieldLabel>{label}</FieldLabel>
    <FieldValue>{value}</FieldValue>
  </div>
);

const renderRawField = (label, value) => (
  <div>
    <FieldLabel>{label}</FieldLabel>
    <RawText>{value}</RawText>
  </div>
);

const renderQuestion = (context) => (
  <QuestionBlock>
    {context.question_image_url && <QuestionImage src={context.question_image_url} alt="Question" />}
    {renderRawField("Question", context.question_text)}
    {context.question_formula && renderRawField("Formula", context.question_formula)}
    {renderField("Options", context.options)}
    {renderField("Correct answer", context.correct_answer)}
  </QuestionBlock>
);

const renderSessionContext = (context) => (
  <ContextDetails>
    <summary>Previous hint, student answers, transcript, tags</summary>
    <ContextDetailsBody>
      {renderField("Previous hint", context.previous_hint)}
      {renderField("Student answers", context.student_answers)}
      {renderField("Transcript", context.transcript)}
      {renderField("Transcript tags", context.transcript_tags)}
      {renderField("Standard", context.standard_description)}
    </ContextDetailsBody>
  </ContextDetails>
);

const renderTutoringStrategies = (context) => (
  <ContextDetails>
    <summary>Tutoring strategies</summary>
    <RawText>{context.available_tutoring_strategies}</RawText>
  </ContextDetails>
);

const CONTEXT_FIELDS = [
  "question_text",
  "question_formula",
  "options",
  "correct_answer",
  "standard_description",
  "previous_hint",
  "student_answers",
  "transcript",
  "transcript_tags",
  "available_tutoring_strategies",
  "question_image_url"
];

const hasContextFields = (context) => CONTEXT_FIELDS.some((field) => context?.[field] != null);

const renderRawInput = (context) =>
  context?.raw_input ? <RawText>{context.raw_input}</RawText> : <NoVal>No input captured.</NoVal>;

const renderContext = (context) => {
  if (!hasContextFields(context)) return renderRawInput(context);
  return (
    <>
      {renderQuestion(context)}
      {renderSessionContext(context)}
      {renderTutoringStrategies(context)}
    </>
  );
};

const anyExpected = (items) => items.some((item) => item.expected);

const runColumnTitle = (label, runName) => (runName ? `${label} · ${shortRunLabel(runName)}` : label);

const renderHint = (output) => (
  <div data-testid="hint-block">
    <FieldLabel>
      Hint (solution_unsolved)
      <WordCount style={output.solution_word_count >= HINT_WORD_LIMIT ? { color: ALERT_RED } : undefined}>
        {output.solution_word_count} words
      </WordCount>
    </FieldLabel>
    <HintText>{output.solution_unsolved}</HintText>
  </div>
);

const renderPills = (output) => (
  <Pills>
    {[output.mnp_strategy_used, output.tutoring_data_tier_utilized]
      .filter((value) => value != null)
      .map((value) => (
        <PillBadge key={value} data-testid="pill">{value}</PillBadge>
      ))}
  </Pills>
);

const renderOptionalField = (label, value) => (value == null ? null : renderField(label, value));

const renderRationales = (output) => (
  <ColumnDetails>
    <summary>Rationales</summary>
    <ColumnDetailsBody>
      {renderOptionalField("Strategy rationale", output.mnp_strategy_rationale)}
      {renderOptionalField("Math explanation rationale", output.math_explanation_rationale)}
    </ColumnDetailsBody>
  </ColumnDetails>
);

const renderAdditional = (additional) => {
  if (!additional || Object.keys(additional).length === 0) return null;
  return (
    <ColumnDetails>
      <summary>Additional fields ({Object.keys(additional).length})</summary>
      <AdditionalBody>{JSON.stringify(additional, null, 2)}</AdditionalBody>
    </ColumnDetails>
  );
};

const renderOutput = (output) => {
  if (!output) return <NoVal>No output</NoVal>;
  if (output.error) return <FieldValue style={{ color: ALERT_RED }}>{output.error}</FieldValue>;
  return (
    <>
      {renderHint(output)}
      {renderPills(output)}
      {renderRationales(output)}
      {renderAdditional(output.additional_fields)}
    </>
  );
};

const renderColumn = (label, title, color, output) => (
  <Column role="group" aria-label={label}>
    <ColumnTitle color={color}>{title}</ColumnTitle>
    {renderOutput(output)}
  </Column>
);

const renderSummary = (label, color, summary) => {
  if (!summary) return null;
  return (
    <SummaryCol>
      <SummaryTitle color={color}>{label}</SummaryTitle>
      <SummaryStat><span className="label">Items</span><span className="val">{summary.item_count ?? "—"}</span></SummaryStat>
      <SummaryStat><span className="label">Avg latency</span><span className="val">{formatLatency(summary.avg_latency_ms)}</span></SummaryStat>
      <SummaryStat><span className="label">Min / Max</span><span className="val">{formatLatency(summary.min_latency_ms)} / {formatLatency(summary.max_latency_ms)}</span></SummaryStat>
      <SummaryStat><span className="label">Total</span><span className="val">{formatLatency(summary.total_latency_ms)}</span></SummaryStat>
    </SummaryCol>
  );
};

export const BetterHintExperimentsComparison = () => {
  const [datasets, setDatasets] = useState([]);
  const [selectedDataset, setSelectedDataset] = useState("");
  const [runs, setRuns] = useState([]);
  const [runA, setRunA] = useState("");
  const [runB, setRunB] = useState("");

  const [loadingDatasets, setLoadingDatasets] = useState(false);
  const [loadingRuns, setLoadingRuns] = useState(false);
  const [loadingComparison, setLoadingComparison] = useState(false);
  const [error, setError] = useState(null);
  const [comparison, setComparison] = useState(null);

  useEffect(() => {
    setLoadingDatasets(true);
    setError(null);
    request("admin/experiments/datasets", { method: "GET" })
      .then((res) => {
        const all = res?.datasets || [];
        setDatasets(all.filter((d) => (d?.name || "").startsWith(DATASET_PREFIX)));
      })
      .catch((e) => {
        logError("Load Langfuse datasets failed", e, { category: LogCategory.ERROR });
        setError(e?.detail || e?.message || "Failed to load datasets");
      })
      .finally(() => setLoadingDatasets(false));
  }, []);

  useEffect(() => {
    if (!selectedDataset) {
      setRuns([]);
      setRunA("");
      setRunB("");
      return;
    }
    setLoadingRuns(true);
    setError(null);
    setRunA("");
    setRunB("");
    request(`admin/experiments/datasets/${encodeURIComponent(selectedDataset)}/runs`, {
      method: "GET",
    })
      .then((res) => {
        setRuns(res?.runs || []);
      })
      .catch((e) => {
        logError("Load Langfuse dataset runs failed", e, { category: LogCategory.ERROR });
        setError(e?.detail || e?.message || "Failed to load runs");
      })
      .finally(() => setLoadingRuns(false));
  }, [selectedDataset]);

  const handleCompare = async () => {
    setLoadingComparison(true);
    setError(null);
    setComparison(null);
    try {
      const res = await request("admin/experiments/compare/better_hint", {
        method: "POST",
        payload: { dataset_name: selectedDataset, run_a: runA, run_b: runB },
      });
      setComparison(res);
    } catch (e) {
      logError("Compare better hint experiments failed", e, { category: LogCategory.ERROR });
      setError(e?.detail || e?.message || "Failed to compare experiments");
    } finally {
      setLoadingComparison(false);
    }
  };

  const showExpected = anyExpected(comparison?.items || []);

  const runOptions = useMemo(
    () =>
      runs.map((r) => (
        <option key={r.id || r.name} value={r.name}>
          {r.name}
        </option>
      )),
    [runs]
  );

  return (
    <Page>
      <h2 style={{ margin: 0 }}>Better Hint Experiments Comparison</h2>
      <div style={{ color: colors.milkGlass600 || "#6b7280", fontSize: 14 }}>
        Compare two Langfuse Better Hint dataset runs side-by-side.
      </div>

      <Controls>
        <ControlField>
          <Form.Label style={{ fontWeight: 600 }}>
            Dataset <span style={{ marginLeft: 6, fontSize: 10, color: "#6b7280" }}>▼</span>
          </Form.Label>
          <Form.Control
            as="select"
            value={selectedDataset}
            onChange={(e) => setSelectedDataset(e.target.value)}
            disabled={loadingDatasets}
          >
            <option value="">
              {loadingDatasets ? "Loading datasets…" : "Select a dataset"}
            </option>
            {datasets.map((d) => (
              <option key={d.id} value={d.name}>
                {d.name}
              </option>
            ))}
          </Form.Control>
        </ControlField>

        <ControlField>
          <Form.Label style={{ fontWeight: 600, color: RUN_A_COLOR }}>
            Run A <span style={{ marginLeft: 6, fontSize: 10 }}>▼</span>
          </Form.Label>
          <Form.Control
            as="select"
            value={runA}
            onChange={(e) => setRunA(e.target.value)}
            disabled={!selectedDataset || loadingRuns}
          >
            <option value="">
              {loadingRuns ? "Loading runs…" : "Select run A"}
            </option>
            {runOptions}
          </Form.Control>
        </ControlField>

        <ControlField>
          <Form.Label style={{ fontWeight: 600, color: RUN_B_COLOR }}>
            Run B <span style={{ marginLeft: 6, fontSize: 10 }}>▼</span>
          </Form.Label>
          <Form.Control
            as="select"
            value={runB}
            onChange={(e) => setRunB(e.target.value)}
            disabled={!selectedDataset || loadingRuns}
          >
            <option value="">
              {loadingRuns ? "Loading runs…" : "Select run B"}
            </option>
            {runOptions}
          </Form.Control>
        </ControlField>

        <div style={{ display: "flex", alignItems: "flex-end" }}>
          <Button
            onClick={handleCompare}
            disabled={!selectedDataset || !runA || !runB || loadingComparison || loadingRuns}
          >
            {loadingComparison ? (
              <>
                <Spinner size="sm" animation="border" /> Comparing…
              </>
            ) : (
              "Compare"
            )}
          </Button>
        </div>
      </Controls>

      {error && <Alert variant="danger">{error}</Alert>}

      {comparison && (
        <>
          {(comparison.run_a_summary || comparison.run_b_summary) && (
            <SummaryRow>
              {renderSummary(shortRunLabel(comparison.run_a), RUN_A_COLOR, comparison.run_a_summary)}
              {renderSummary(shortRunLabel(comparison.run_b), RUN_B_COLOR, comparison.run_b_summary)}
            </SummaryRow>
          )}

          <div style={{ fontSize: 14, color: colors.milkGlass600 || "#6b7280" }}>
            {comparison.items.length} item(s) compared
          </div>

          {comparison.items.map((item, idx) => (
            <ItemCard key={item.dataset_item_id} data-testid="better-hint-item">
              <ItemHeader>
                <div>
                  <ItemIndex>#{idx + 1}</ItemIndex>{" "}
                  <ItemId>{item.dataset_item_id}</ItemId>
                </div>
                <div style={{ fontSize: 12, color: "#6b7280" }}>
                  <span style={{ color: RUN_A_COLOR, fontWeight: 600 }}>{shortRunLabel(comparison.run_a)}</span>{" "}
                  {formatLatency(item.run_a_latency_ms)}
                  {"  ·  "}
                  <span style={{ color: RUN_B_COLOR, fontWeight: 600 }}>{shortRunLabel(comparison.run_b)}</span>{" "}
                  {formatLatency(item.run_b_latency_ms)}
                </div>
              </ItemHeader>

              {renderContext(item.context)}

              <ColumnsGrid $columns={showExpected ? 3 : 2}>
                {showExpected && renderColumn("Expected", "Expected", EXPECTED_COLOR, item.expected)}
                {renderColumn("Run A", runColumnTitle("Run A", comparison.run_a), RUN_A_COLOR, item.run_a)}
                {renderColumn("Run B", runColumnTitle("Run B", comparison.run_b), RUN_B_COLOR, item.run_b)}
              </ColumnsGrid>
            </ItemCard>
          ))}
        </>
      )}
    </Page>
  );
};
